import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, ArrowRight, History, Info } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  Confronto anagrafica per anno (richiesta titolare 14/09/2026).

  Aprendo un esercizio vecchio si trovano nomi che oggi non ci sono piu': nel
  2019 l'unita' A15 e' di Lo Sicco Giovanna, non di Maniscalchi Elisa. Le unita'
  immobiliari invece sono sempre le stesse. Questa scheda tiene ferme le unita'
  in colonna e affianca, esercizio per esercizio, i nominativi che il rendiconto
  di quell'anno attribuisce a ciascuna.

  E' una ricostruzione documentale: viene dalle scansioni dei rendiconti, NON
  dall'anagrafica e non la modifica. L'anagrafica resta quella dell'app.
*/

const RUOLI = {
  Pr: { label: 'proprietario', color: 'bg-blue-100 text-blue-800' },
  Np: { label: 'nudo proprietario', color: 'bg-purple-100 text-purple-800' },
  Us: { label: 'usufruttuario', color: 'bg-amber-100 text-amber-800' },
  Co: { label: 'conduttore / inquilino', color: 'bg-green-100 text-green-800' },
  '?': { label: 'ruolo non leggibile nella scansione', color: 'bg-stone-200 text-stone-700' },
};

const TITOLI = { tab_millesimale: 'Tab. millesimale', app_oggi: 'App oggi' };
const etichettaColonna = (c) => TITOLI[c] || c;

const euro = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

const chiaveUnita = (n) => {
  const m = /^([A-Za-z]*)(\d+)/.exec(String(n || ''));
  return m ? [m[1], parseInt(m[2], 10)] : [String(n || ''), 0];
};

// Identita' confrontabile di una posizione: l'id della persona in anagrafica
// quando la ricostruzione e' riuscita ad agganciarla, altrimenti il nome.
const identita = (voce) => {
  const agganci = voce?.soggetti_app || [];
  if (agganci.length) return agganci.map((a) => a.id);
  return [(voce?.nome || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim()];
};

const proprietari = (voci = []) => {
  const vivi = voci.filter((v) => !v.cessato);
  const pr = vivi.filter((v) => v.ruolo === 'Pr' || v.ruolo === 'Np');
  if (pr.length) return pr;
  const incerti = vivi.filter((v) => v.ruolo === '?');
  if (incerti.length) return incerti;
  return vivi.length ? vivi : voci;
};

const stessiTitolari = (a = [], b = []) => {
  const sa = new Set(proprietari(a).flatMap(identita));
  const sb = new Set(proprietari(b).flatMap(identita));
  if (sa.size !== sb.size) return false;
  for (const k of sa) if (!sb.has(k)) return false;
  return true;
};

export const TimelineTab = ({ condominiumId }) => {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [cerca, setCerca] = useState('');
  const [soloCambi, setSoloCambi] = useState(false);
  const [aperta, setAperta] = useState(null);

  useEffect(() => {
    let vivo = true;
    setCaricando(true);
    setErrore(null);
    axios
      .get(`${API}/condominiums/${condominiumId}/ownership-timeline`)
      .then((r) => vivo && setDati(r.data))
      .catch((e) => vivo && setErrore(e?.response?.status === 404 ? 'assente' : 'errore'))
      .finally(() => vivo && setCaricando(false));
    return () => {
      vivo = false;
    };
  }, [condominiumId]);

  const colonne = useMemo(() => {
    if (!dati?.unita) return [];
    const viste = [];
    Object.values(dati.unita).forEach((riga) =>
      Object.keys(riga).forEach((k) => {
        if (['quote_app', 'solo_in_scansione'].includes(k)) return;
        if (!viste.includes(k)) viste.push(k);
      })
    );
    return viste;
  }, [dati]);

  const righe = useMemo(() => {
    if (!dati?.unita) return [];
    const testo = cerca.trim().toLowerCase();
    return Object.entries(dati.unita)
      .map(([codice, riga]) => {
        let precedente = null;
        const celle = colonne.map((col) => {
          const voci = riga[col]?.voci || [];
          const cambio = voci.length && precedente && !stessiTitolari(precedente, voci);
          if (voci.length) precedente = voci;
          return { col, voci, cambio: Boolean(cambio) };
        });
        return { codice, riga, celle, cambi: celle.filter((c) => c.cambio).length };
      })
      .filter((r) => (soloCambi ? r.cambi > 0 : true))
      .filter((r) => {
        if (!testo) return true;
        if (r.codice.toLowerCase().includes(testo)) return true;
        return r.celle.some((c) =>
          c.voci.some((v) => (v.nome || '').toLowerCase().includes(testo))
        );
      })
      .sort((a, b) => {
        const [la, na] = chiaveUnita(a.codice);
        const [lb, nb] = chiaveUnita(b.codice);
        return la.localeCompare(lb) || na - nb;
      });
  }, [dati, colonne, cerca, soloCambi]);

  if (caricando) {
    return <p className="text-sm text-stone-500" data-testid="timeline-loading">Carico la scheda…</p>;
  }

  if (errore === 'assente') {
    return (
      <Card data-testid="timeline-empty">
        <CardContent className="py-10 text-center space-y-2">
          <History className="h-8 w-8 mx-auto text-stone-400" />
          <p className="font-medium">Nessuna ricostruzione caricata</p>
          <p className="text-sm text-stone-500 max-w-xl mx-auto">
            La scheda si costruisce dalle scansioni dei rendiconti ordinari, non
            si genera da sola. Va caricata una volta; poi resta qui.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (errore) {
    return (
      <p className="text-sm text-red-700" data-testid="timeline-error">
        Non sono riuscito a leggere la scheda.
      </p>
    );
  }

  const dettaglio = aperta ? dati.unita[aperta] : null;

  return (
    <div className="space-y-6" data-testid="timeline-tab">
      <Card className="border-l-4 border-l-[#0D4D4F]">
        <CardContent className="py-4 flex gap-3 text-sm text-stone-700">
          <Info className="h-5 w-5 shrink-0 text-[#0D4D4F]" />
          <p>
            Le unità immobiliari restano ferme, i nomi cambiano. Ogni colonna è
            un documento: si legge in orizzontale chi il rendiconto di
            quell'anno indica come titolare.{' '}
            <strong>Questa scheda non è l'anagrafica e non la modifica</strong>:
            serve a leggere gli esercizi vecchi senza cercarci le persone di
            oggi.
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={cerca}
          onChange={(e) => setCerca(e.target.value)}
          placeholder="Cerca un'unità o un nominativo…"
          className="max-w-xs"
          data-testid="timeline-search"
        />
        <Button
          variant={soloCambi ? 'default' : 'outline'}
          onClick={() => setSoloCambi((v) => !v)}
          data-testid="timeline-only-changes"
        >
          Solo le unità dove il nome cambia
        </Button>
        <span className="text-sm text-stone-500">
          {righe.length} unità su {Object.keys(dati.unita).length}
        </span>
      </div>

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm border-collapse" data-testid="timeline-matrix">
          <thead>
            <tr className="bg-stone-50">
              <th className="sticky left-0 z-10 bg-stone-50 text-left px-3 py-2 font-semibold border-b border-r">
                Unità
              </th>
              {colonne.map((c) => (
                <th key={c} className="text-left px-3 py-2 font-semibold border-b whitespace-nowrap">
                  {etichettaColonna(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {righe.map(({ codice, celle, cambi }) => (
              <tr
                key={codice}
                className="align-top hover:bg-stone-50 cursor-pointer"
                onClick={() => setAperta(aperta === codice ? null : codice)}
                data-testid={`timeline-row-${codice}`}
              >
                <th className="sticky left-0 z-10 bg-white text-left px-3 py-2 font-semibold border-b border-r whitespace-nowrap">
                  {codice}
                  {cambi > 0 && (
                    <span className="ml-2 text-xs font-normal text-amber-700">{cambi} ⟶</span>
                  )}
                </th>
                {celle.map(({ col, voci, cambio }) => (
                  <td
                    key={col}
                    className={`px-3 py-2 border-b ${cambio ? 'bg-amber-50' : ''}`}
                  >
                    {proprietari(voci).length === 0 ? (
                      <span className="text-stone-400">—</span>
                    ) : (
                      proprietari(voci).map((v, i) => (
                        <div key={i} className="whitespace-nowrap">
                          {v.nome}
                          {v.ruolo !== 'Pr' && (
                            <span className="text-stone-500"> ({v.ruolo})</span>
                          )}
                          {v.cessato && <span className="text-stone-400"> · chiusa</span>}
                        </div>
                      ))
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dettaglio && (
        <Card data-testid="timeline-detail">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Unità {aperta}
              <span className="text-sm font-normal text-stone-500">
                tutte le posizioni, documento per documento
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              {colonne.map((col) => {
                const voci = dettaglio[col]?.voci || [];
                return (
                  <div key={col} className="flex flex-col sm:flex-row gap-2">
                    <div className="sm:w-40 shrink-0 text-sm font-medium text-stone-600">
                      {etichettaColonna(col)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {voci.length === 0 && <span className="text-sm text-stone-400">—</span>}
                      {voci.map((v, i) => (
                        <Badge
                          key={i}
                          variant="secondary"
                          className={`${RUOLI[v.ruolo]?.color || ''} font-normal`}
                          title={`${RUOLI[v.ruolo]?.label || ''}${
                            v.testo_scansione ? ` · scansione: ${v.testo_scansione}` : ''
                          }`}
                        >
                          {v.nome}
                          {v.dal ? ` · dal ${v.dal}` : ''}
                          {v.cessato ? ' · chiusa' : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <QuoteFuoriPosto riga={dettaglio} colonne={colonne} />
          </CardContent>
        </Card>
      )}

      {dati.fonti && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Da dove viene ogni colonna</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {colonne.map((c) => (
              <div key={c} className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                <span className="sm:w-40 shrink-0 font-medium text-stone-600">
                  {etichettaColonna(c)}
                </span>
                <span className="text-stone-600">{dati.fonti[c] || '—'}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Il ponte con le quote: la quota porta scritto addosso un nome, congelato
// quando la quota e' stata generata. Qui si vede dove quel nome non e' fra i
// titolari che il rendiconto di quell'anno attribuisce all'unita'.
const QuoteFuoriPosto = ({ riga, colonne }) => {
  const fuori = useMemo(() => {
    const out = [];
    Object.entries(riga.quote_app || {}).forEach(([anno, quote]) => {
      if (!colonne.includes(anno)) return;
      const voci = riga[anno]?.voci || [];
      if (!voci.length) return;
      const attesi = new Set(voci.flatMap(identita));
      quote.forEach((q) => {
        if (!identita(q).some((k) => attesi.has(k))) out.push({ anno, ...q });
      });
    });
    return out.sort((a, b) => a.anno.localeCompare(b.anno));
  }, [riga, colonne]);

  if (!fuori.length) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
        <AlertTriangle className="h-4 w-4" />
        Quote intestate a chi, quell'anno, non risulta titolare
      </div>
      <table className="text-sm w-full max-w-2xl">
        <thead>
          <tr className="text-left text-stone-500">
            <th className="py-1 pr-4 font-medium">Anno</th>
            <th className="py-1 pr-4 font-medium">Nome sulla quota</th>
            <th className="py-1 pr-4 font-medium">Mesi</th>
            <th className="py-1 font-medium text-right">Importo</th>
          </tr>
        </thead>
        <tbody>
          {fuori.map((q, i) => (
            <tr key={i} className="border-t">
              <td className="py-1 pr-4">{q.anno}</td>
              <td className="py-1 pr-4 flex items-center gap-1">
                {q.nome}
                <ArrowRight className="h-3 w-3 text-stone-400" />
              </td>
              <td className="py-1 pr-4">{q.mesi}</td>
              <td className="py-1 text-right tabular-nums">{euro(q.importo)} €</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-stone-500 max-w-2xl">
        Il nome sulla quota è una copia del testo, congelata al momento in cui la
        quota è stata generata: non segue l'anagrafica e non guarda il periodo.
        Questa tabella dice dove non combacia; non corregge niente.
      </p>
    </div>
  );
};
